import { Box, HBox, VBox } from "@ui/box/Box"
import { Spinner } from "@ui/spinner/Spinner"
import { Typo } from "@ui/text/Typo"
import { formatCurrencyWithNumberToSymbolWithNumber } from "@ui/utils/formatter"
import { useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import { GD } from "src/GD"
import { ActionButton, DS } from "../ds/Kit"
import { CardSubView } from "./CardsList"
import { parseCard3dsApiDate } from "src/utils/card3dsDates"
import { getFriendlyErrorMessage } from "src/utils/errors"

const CARD_3DS_POLL_INTERVAL_MS = 10000
const CARD_3DS_FALLBACK_LIFETIME_MS = 15 * 60 * 1000

const getExpiryTime = (code: ICard3dsCode) => {
	if (code.expires_at) return parseCard3dsApiDate(code.expires_at).getTime()
	return parseCard3dsApiDate(code.transaction_time).getTime() + CARD_3DS_FALLBACK_LIFETIME_MS
}

const formatRemainingTime = (expiresAt: number, now: number) => {
	const remainingSeconds = Math.max(0, Math.ceil((expiresAt - now) / 1000))
	const minutes = Math.floor(remainingSeconds / 60)
	const seconds = remainingSeconds % 60
	return `${minutes}:${String(seconds).padStart(2, "0")}`
}

const CODE_TYPE_LABELS: Record<TCard3dsCodeType, string> = {
	third_3ds_otp: "3DS code",
	auth_url: "Authentication link",
	activation_code: "Activation code"
}

export const Card3dsCodes = ({ cardUid, card, onBack }: { cardUid: string; card?: ICardSaved; onBack?: () => void }) => {
	const [codes, setCodes] = useState<ICard3dsCode[]>([])
	const [busy, setBusy] = useState(true)
	const [error, setError] = useState<unknown>()
	const [now, setNow] = useState(() => Date.now())

	const loadCodes = useCallback(async (active: () => boolean) => {
		try {
			const response = await GD.REQ_CARD_3DS_LIST.request({
				card_uid: cardUid,
				limit: 3,
				include_expired: false
			})
			if (!active()) return
			if (response.error) {
				setError(response.error)
				return
			}
			setCodes(response.data)
			setError(undefined)
		} catch (nextError) {
			if (active()) setError(nextError)
		} finally {
			if (active()) setBusy(false)
		}
	}, [cardUid])

	useEffect(() => {
		let active = true
		let pollTimer: number | undefined
		const isActive = () => active
		const initialize = async () => {
			try {
				await GD.REQ_CARD_3DS_REFRESH.request({ card_uid: cardUid, minutes: 30 })
			} finally {
				await loadCodes(isActive)
				if (active) {
					pollTimer = window.setInterval(
						() => void loadCodes(isActive),
						CARD_3DS_POLL_INTERVAL_MS
					)
				}
			}
		}
		void initialize()
		const countdownTimer = window.setInterval(() => setNow(Date.now()), 1000)

		return () => {
			active = false
			if (pollTimer !== undefined) window.clearInterval(pollTimer)
			window.clearInterval(countdownTimer)
		}
	}, [cardUid, loadCodes])

	const visibleCodes = useMemo(
		() => codes.filter((code) => !code.expired && getExpiryTime(code) > now),
		[codes, now]
	)

	return (
		<Box sx={{ width: "100%", padding: "20px", minWidth: 0 }}>
			<CardSubView
				title='3DS codes'
				description='New codes appear automatically every 10 seconds.'
				card={card}
				onBack={onBack}
			>
				{busy ? (
					<VBox sx={{ minHeight: "180px", alignItems: "center", justifyContent: "center" }}>
						<Spinner color={DS.accent} />
					</VBox>
				) : error ? (
					<Box role='alert' sx={{ padding: "16px", borderRadius: DS.radius, backgroundColor: "#FEF2F2", border: "1px solid #FECACA" }}>
						<Typo sx={{ color: "#B91C1C" }}>{getFriendlyErrorMessage(error, "Could not load 3DS codes.")}</Typo>
					</Box>
				) : visibleCodes.length === 0 ? (
					<VBox gap='6px' sx={{ padding: "36px 20px", alignItems: "center", textAlign: "center", borderRadius: DS.radius, backgroundColor: DS.soft, border: `1px dashed ${DS.line}` }}>
						<Typo sx={{ color: DS.ink, fontWeight: 600 }}>No active codes yet</Typo>
						<Typo sx={{ color: DS.muted, fontSize: "13px" }}>Keep this window open. The list refreshes automatically.</Typo>
					</VBox>
				) : (
					<VBox gap='12px'>
						{visibleCodes.map((code) => {
							const expiresAt = getExpiryTime(code)
							const isLink = code.type === "auth_url" && /^https?:\/\//i.test(code.value)
							const expiring = expiresAt - now < 60000
							return (
								<VBox key={code.uid} gap='14px' sx={{ padding: "16px 18px", borderRadius: DS.radius, border: `1px solid ${DS.line}`, background: DS.surface, boxShadow: DS.shadow }}>
									<HBox sx={{ alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }} gap='10px'>
										<Typo sx={{ color: DS.muted, fontSize: "13px", fontWeight: 600 }}>{CODE_TYPE_LABELS[code.type]}</Typo>
										<Typo sx={{ fontSize: "12px", fontWeight: 600, padding: "3px 10px", borderRadius: "999px", color: expiring ? "#B91C1C" : "#047857", background: expiring ? "#FEF2F2" : "#ECFDF5", fontVariantNumeric: "tabular-nums" }}>Expires in {formatRemainingTime(expiresAt, now)}</Typo>
									</HBox>
									<HBox sx={{ alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }} gap='12px'>
										{isLink ? (
											<a href={code.value} target='_blank' rel='noreferrer' style={{ color: DS.accentInk, fontWeight: 700, overflowWrap: "anywhere" }}>Open authentication link</a>
										) : (
											<Typo sx={{ color: DS.ink, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: "28px", fontWeight: 700, letterSpacing: "0.12em", overflowWrap: "anywhere", fontVariantNumeric: "tabular-nums" }}>{code.value}</Typo>
										)}
										<ActionButton onClick={() => void navigator.clipboard.writeText(code.value).then(() => toast.success("Code copied."))}>Copy</ActionButton>
									</HBox>
									{(code.merchant_name || (code.amount !== null && code.currency) || code.transaction_time) && (
										<VBox gap='2px' sx={{ paddingTop: "12px", borderTop: `1px solid ${DS.line}` }}>
											{code.merchant_name && <Typo sx={{ color: DS.ink, fontWeight: 600, fontSize: "14px" }}>{code.merchant_name}</Typo>}
											{code.amount !== null && code.currency && <Typo sx={{ color: DS.text, fontSize: "13px", fontVariantNumeric: "tabular-nums" }}>{formatCurrencyWithNumberToSymbolWithNumber(code.amount, code.currency)}</Typo>}
											<Typo sx={{ color: DS.muted, fontSize: "12px" }}>{parseCard3dsApiDate(code.transaction_time).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</Typo>
										</VBox>
									)}
								</VBox>
							)
						})}
					</VBox>
				)}
			</CardSubView>
		</Box>
	)
}
